import type { ApiOrganization } from '@repo/api-kit/queries/register-organization';
import {
  DEFAULT_ZIPCODE_REGEX,
  FIVE_DIGITS_COUNTRIES,
  FIVE_DIGITS_REGEX,
} from '../../../../common/validations/primitives';

const FIVE_DIGITS_COUNTRY_SET: ReadonlySet<string> = new Set(FIVE_DIGITS_COUNTRIES);

export interface AddressReadOnlyFlags {
  addressLineOne: boolean;
  addressZipcode: boolean;
  addressCity: boolean;
  addressCountry: boolean;
}

function hasValue(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function comesFromThirdParty(organization: ApiOrganization): boolean {
  return !organization.unreferenced && !organization.underRegistration;
}

export function isValidAddressLineOne(value: string | null | undefined): boolean {
  return hasValue(value) && value.trim().length >= 2;
}

export function isValidZipcode(
  zipcode: string | null | undefined,
  country: string | null | undefined
): boolean {
  if (!hasValue(zipcode)) {
    return false;
  }

  const regex =
    country && FIVE_DIGITS_COUNTRY_SET.has(country) ? FIVE_DIGITS_REGEX : DEFAULT_ZIPCODE_REGEX;

  return regex.test(zipcode);
}

export function isValidAddressCity(value: string | null | undefined): boolean {
  return hasValue(value);
}

export function isValidAddressCountry(value: string | null | undefined): boolean {
  return hasValue(value);
}

export function getReadOnlyFlags(organization: ApiOrganization): AddressReadOnlyFlags {
  const isThirdPartyAddress = comesFromThirdParty(organization);

  return {
    addressLineOne: isThirdPartyAddress && isValidAddressLineOne(organization.addressLine1),
    addressZipcode:
      isThirdPartyAddress && isValidZipcode(organization.addressZipcode, organization.legalCountry),
    addressCity: isThirdPartyAddress && isValidAddressCity(organization.addressCity),
    addressCountry: isThirdPartyAddress && isValidAddressCountry(organization.addressCountry),
  };
}
